// <product-gallery>: thumbnail -> main stage swap; variant sync via gallery:select;
// opens <product-lightbox> and honors a ?img=<media.id> deep-link on load.
customElements.define(
  "product-gallery",
  class extends HTMLElement {
    connectedCallback() {
      this.stages = this.querySelectorAll("[data-media]");
      this.addEventListener("click", (e) => {
        const thumb = e.target.closest("[data-media-id]");
        if (thumb) { this.select(thumb.dataset.mediaId); return; }
        const opener = e.target.closest("[data-lb-open]");
        if (opener) this.openLightbox(opener.dataset.lbId);
      });
      document.addEventListener("gallery:select", (e) => this.select(String(e.detail.id)));

      // Thumbnail rail: arrows scroll the list (vertical on desktop, horizontal on
      // mobile), disable at the ends, and hide when there's no overflow.
      //
      // The rail's HEIGHT is not set here. It used to be -- rail.style.height =
      // mainStage.offsetHeight -- but this script is deferred, so the desktop rail painted
      // at its natural height first (every thumb on show, 279px taller than the image at
      // 1440x900) and then collapsed onto it. The rail is out of flow with lg:inset-y-0
      // now, which gives it the same height with no script and no shift. All that is left
      // for the observer is the arrow state, which genuinely depends on measurement.
      this.scroller = this.querySelector("[data-thumb-scroll]");
      this.prevBtn = this.querySelector("[data-thumb-prev]");
      this.nextBtn = this.querySelector("[data-thumb-next]");
      this.mainStage = this.querySelector("[data-main-stage]");
      if (this.scroller && this.prevBtn && this.nextBtn) {
        this.prevBtn.addEventListener("click", () => this.scrollThumbs(-1));
        this.nextBtn.addEventListener("click", () => this.scrollThumbs(1));
        this.scroller.addEventListener("scroll", () => this.updateArrows(), { passive: true });
        // The image resizing changes how many thumbs fit, hence whether the arrows apply.
        this.ro = new ResizeObserver(() => this.updateArrows());
        if (this.mainStage) this.ro.observe(this.mainStage);
        this.updateArrows();
      }

      const img = new URLSearchParams(location.search).get("img");
      if (img) {
        this.select(img);
        customElements.whenDefined("product-lightbox").then(() => {
          const lb = document.querySelector("product-lightbox");
          if (lb) lb.openTo(img);
        });
      }
    }
    select(id) {
      this.stages.forEach((s) => s.classList.toggle("is-active", s.dataset.media === id));
      let active = null;
      this.querySelectorAll("[data-media-id]").forEach((t) => {
        const on = t.dataset.mediaId === id;
        t.classList.toggle("is-active", on);
        if (on) active = t;
      });
      this.revealThumb(active);
    }
    // Marking a thumb active is not enough when the rail cannot show it: the strip holds
    // about four of twelve thumbs on a phone and the vertical list about five, so a
    // variant whose image sits further down changed the main image while the rail sat
    // exactly where it was -- reading as a selector that had not moved at all.
    //
    // scrollBy on the rail, not thumb.scrollIntoView(): scrollIntoView scrolls EVERY
    // ancestor scrollport, and the nearest one on a product page is the document, so
    // picking a variant would jump the whole page. Only fires when the thumb is not
    // already fully in view, so tapping a visible thumb does not shuffle the rail out
    // from under the finger that tapped it. The rail's own scroll listener re-derives the
    // arrow state from the scroll this causes.
    revealThumb(thumb) {
      const s = this.scroller;
      if (!s || !thumb) return;
      // Same axis test the arrows use: below lg the rail is a horizontal strip, above it
      // a vertical list.
      const vertical = s.scrollHeight - s.clientHeight > 1;
      if (!vertical && s.scrollWidth - s.clientWidth <= 1) return;
      const t = thumb.getBoundingClientRect();
      const c = s.getBoundingClientRect();
      const inView = vertical
        ? t.top >= c.top - 1 && t.bottom <= c.bottom + 1
        : t.left >= c.left - 1 && t.right <= c.right + 1;
      if (inView) return;
      // Centre it; the scroller clamps this itself at either end.
      const delta = vertical
        ? t.top + t.height / 2 - (c.top + c.height / 2)
        : t.left + t.width / 2 - (c.left + c.width / 2);
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth";
      s.scrollBy(vertical ? { top: delta, behavior } : { left: delta, behavior });
    }
    scrollThumbs(dir) {
      const s = this.scroller;
      const vertical = s.scrollHeight - s.clientHeight > 1;
      const thumb = s.querySelector("[data-media-id]");
      const size = thumb ? (vertical ? thumb.offsetHeight : thumb.offsetWidth) : 80;
      const step = (size + 12) * 2; // gap-3 = 12px; ~2 thumbs per click
      this.scroller.scrollBy(vertical ? { top: dir * step, behavior: "smooth" } : { left: dir * step, behavior: "smooth" });
    }
    updateArrows() {
      const s = this.scroller;
      if (!s) return;
      const vertical = s.scrollHeight - s.clientHeight > 1;
      const horizontal = s.scrollWidth - s.clientWidth > 1;
      const overflow = vertical || horizontal;
      const display = overflow ? "" : "none";
      this.prevBtn.style.display = display;
      this.nextBtn.style.display = display;
      if (!overflow) return;
      const pos = vertical ? s.scrollTop : s.scrollLeft;
      const max = vertical ? s.scrollHeight - s.clientHeight : s.scrollWidth - s.clientWidth;
      this.prevBtn.disabled = pos <= 1;
      this.nextBtn.disabled = pos >= max - 1;
    }
    openLightbox(id) {
      const lb = document.querySelector("product-lightbox");
      if (lb) lb.openTo(id);
    }
  }
);
